import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../shared/api-response';
import { BusinessAccount, ConnectedUser } from '../shared/connected-user';
import { UserSessionService } from '../user-session.service';

// ===== Types d'auth =====
export interface LoginPayload { username: string; password: string; deviceId?: string; }
export interface DeviceChallengeResponse {
  deviceValidationRequired: boolean;
  challengeToken: string;
  expiresAt: string;
  deliveryChannel: string;
  maskedTarget: string;
  message: string;
}
export interface DeviceVerifyPayload {
  username: string;
  challengeToken: string;
  otp: string;
  deviceId?: string;
}
export type LoginResponse = JwtResponse | DeviceChallengeResponse;
export interface JwtResponse {
  accessToken: string;
  refreshToken: string;
  username: string;
  firstname: string;
  phone: string;
  lastname: string;
  roles: string[];
  confirmCNi: boolean;
  confirmphone: boolean;
  secure: boolean;
  secretQuestion?: string;
  lang: string;
  // Compte principal
  primaryAccountNumber?: string;
  primaryAccountBalance?: string | number;
  primaryAccountSigle?: string;


  // Business accounts
  businessAccounts: BusinessAccount[];
  partnerSupervisor?: boolean;
  partnerActivationRequired?: boolean;
  partnerMatriculeEmploye?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private host: string = environment.apiUrl;
  private jwtToken: string = null;
  private roles: Array<any>;
  private readonly deviceIdStorageKey = 'spay_trusted_device_id';

  constructor(private http: HttpClient, private userSession: UserSessionService) { }

  saveUsername(username: string) {
    localStorage.setItem('usernameThatUserIsNowConnect2345789', username);
  }

  findEmail(email: string): Observable<ApiResponse<void>> {
    return this.http.get<ApiResponse<void>>(this.host + "/user/anonym/isEmail/" + email);
  }

  findPhone(phone: string): Observable<ApiResponse<void>> {
    return this.http.get<ApiResponse<void>>(this.host + "/user/anonym/isPhone/" + phone);
  }

  saveUser(user): Observable<ApiResponse<number>> {
    const payload = { ...user, deviceId: user?.deviceId || this.getOrCreateDeviceId() };
    return this.http.post<ApiResponse<number>>(this.host + "/user/anonym/addUser", payload, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  getlistPays() {
    return this.http.get(this.host + "/user/anonym/pays/all", { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  requestPasswordReset(identifier: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(this.host + "/user/anonym/password/reset/request",
      { identifier },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  confirmPasswordReset(data: { token: string; password: string; }): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(this.host + "/user/anonym/password/reset/confirm",
      data,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  login(user: LoginPayload): Observable<any> {
    const payload: LoginPayload = {
      ...user,
      deviceId: user?.deviceId || this.getOrCreateDeviceId()
    };
    return this.http.post<any>(this.host + "/spay/auth/login", payload, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  verifyTrustedDevice(payload: DeviceVerifyPayload): Observable<JwtResponse> {
    const request: DeviceVerifyPayload = {
      ...payload,
      deviceId: payload?.deviceId || this.getOrCreateDeviceId()
    };
    return this.http.post<JwtResponse>(this.host + "/spay/auth/device/verify", request, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  refresh(refreshToken: string): Observable<JwtResponse> {
    return this.http.post<JwtResponse>(this.host + "/spay/auth/refresh", { refreshToken }, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }



  saveConnectedUseralt(data: Partial<JwtResponse> & { token?: string }): void {

    const rawToken = (data.accessToken || (data as any).token || '').toString().trim();
    const cleanToken = rawToken ? rawToken.replace(/^CBCL\s+/i, '').trim() : '';

    // Si tu veux absolument un token pour considérer "connecté"
    if (cleanToken) {
      localStorage.setItem('tokenConnectedUserWSU1234567GHTVB', cleanToken);
    }

    const connectedUser: ConnectedUser = {
      username: data.username ?? '',
      firstname: data.firstname ?? '',
      lastname: data.lastname ?? '',
      phone: data.phone ?? '',
      roles: (data.roles ?? this.roles ?? []).filter(Boolean),

      confirmCNi: Boolean(data.confirmCNi ?? false),
      confirmphone: Boolean(data.confirmphone ?? false),
      secure: Boolean(data.secure ?? false),
      secretQuestion: data.secretQuestion ?? null,
      partnerSupervisor: Boolean(data.partnerSupervisor ?? false),
      partnerActivationRequired: Boolean(data.partnerActivationRequired ?? false),
      partnerMatriculeEmploye: data.partnerMatriculeEmploye ?? undefined,

      // Tokens
      token: cleanToken || undefined,
      refreshToken: data.refreshToken,

      lang: data.lang ?? 'fr',

      primaryAccountNumber: data.primaryAccountNumber,
      primaryAccountBalance: data.primaryAccountBalance,
      primaryAccountSigle: data.primaryAccountSigle,

      businessAccounts: data.businessAccounts ?? [],
    };

    this.userSession.setUser(connectedUser);
  }
  private extractRolesFromToken(token: string): string[] {
    if (!token) return [];

    // JWT attendu: 3 segments
    const parts = token.split('.');
    if (parts.length !== 3) return [];

    try {
      const jwtHelper = new JwtHelperService();
      const decoded: any = jwtHelper.decodeToken(token) || {};

      const rawRoles = decoded.roles ?? decoded.authorities ?? decoded.authority ?? [];
      const asArray = Array.isArray(rawRoles) ? rawRoles : [rawRoles];

      const normalized = asArray
        .map((v: any) => {
          if (!v) return null;
          if (typeof v === 'string') return v;
          return v.authority || v.roleName || v.name || v.role || null;
        })
        .filter(Boolean) as string[];

      // dédoublonnage
      return Array.from(new Set(normalized));
    } catch {
      return [];
    }
  }

  saveConnectedUser(data: Partial<JwtResponse> & { token?: string }): void {
    const current = this.userSession.getUser();

    // 1) Token : accessToken ou token
    const rawToken = String((data.accessToken || (data as any).token || '')).trim();
    const cleanToken = rawToken ? rawToken.replace(/^CBCL\s+/i, '').trim() : '';

    // 2) Roles : priorité JWT, fallback data.roles
    const rolesFromJwt = cleanToken ? this.extractRolesFromToken(cleanToken) : [];
    const rolesFromBackend = (data.roles ?? []).filter(Boolean);
    const finalRoles = rolesFromJwt.length
      ? rolesFromJwt
      : (rolesFromBackend.length ? rolesFromBackend : (current?.roles ?? []));

    // 3) Construire ConnectedUser (sans spread hasardeux)
    const connectedUser: ConnectedUser = {
      username: data.username ?? current?.username ?? '',
      firstname: data.firstname ?? current?.firstname ?? '',
      lastname: data.lastname ?? current?.lastname ?? '',
      phone: data.phone ?? current?.phone ?? '',
      roles: finalRoles,

      confirmCNi: Boolean(data.confirmCNi ?? current?.confirmCNi ?? false),
      confirmphone: Boolean(data.confirmphone ?? current?.confirmphone ?? false),
      secure: Boolean(data.secure ?? current?.secure ?? false),
      secretQuestion: data.secretQuestion ?? current?.secretQuestion ?? null,
      partnerSupervisor: data.partnerSupervisor ?? current?.partnerSupervisor ?? false,
      partnerActivationRequired: data.partnerActivationRequired ?? current?.partnerActivationRequired ?? false,
      partnerMatriculeEmploye: data.partnerMatriculeEmploye ?? current?.partnerMatriculeEmploye ?? undefined,

      // Tokens
      token: cleanToken || current?.token || undefined,
      refreshToken: data.refreshToken ?? current?.refreshToken,

      lang: data.lang ?? current?.lang ?? 'fr',

      // Compte principal
      primaryAccountNumber: data.primaryAccountNumber ?? current?.primaryAccountNumber,
      primaryAccountBalance: data.primaryAccountBalance ?? current?.primaryAccountBalance,
      primaryAccountSigle: data.primaryAccountSigle ?? current?.primaryAccountSigle,

      // Business accounts
      businessAccounts: data.businessAccounts ?? current?.businessAccounts ?? [],
    };

    // 4) Stockage token “global” (si tu en as encore besoin pour isAuthenticated)
    if (cleanToken) {
      localStorage.setItem('tokenConnectedUserWSU1234567GHTVB', cleanToken);
    }
    if (connectedUser.refreshToken) {
      localStorage.setItem('refreshToken', connectedUser.refreshToken);
    }

    // 5) Push session (cache + observable si tu l’ajoutes)
    this.userSession.setUser(connectedUser);
  }



  savetoken(jwt: string, secure: boolean, confirmphone: boolean, confirmCNi: boolean): string[] {
    const raw = jwt?.replace(/^CBCL\s+/i, '').trim() || '';
    this.jwtToken = raw;

    const jwtHelper = new JwtHelperService();
    const decoded: any = jwtHelper.decodeToken(raw) || {};
    const rawRoles: any[] = decoded.roles || decoded.authorities || [];

    const authorities = Array.from(new Set(
      rawRoles
        .map(v => typeof v === 'string' ? v : v?.authority || v?.roleName || v)
        .filter(Boolean)
    ));

    this.roles = authorities;

    // ... tes localStorage selon rôles (inchangé)
    // (mais utilise raw, pas jwt)

    return authorities;
  }


  savetokenalt(jwt: string, secure: boolean, confirmphone: boolean, confirmCNi: boolean) {
    const raw = jwt?.replace(/^CBCL\s+/i, '') || jwt;
    this.jwtToken = raw;
    const jwtHelper = new JwtHelperService();
    const decoded: any = jwtHelper.decodeToken(this.jwtToken) || {};
    const rawRoles: any[] = decoded.roles || decoded.authorities || [];

    // normalise et dédoublonne les rôles
    const authorities = Array.from(new Set(
      rawRoles.map((v: any) => typeof v === 'string' ? v : v?.authority || v?.roleName || v)
        .filter((v: any) => !!v)
    ));

    this.roles = authorities;

    authorities.forEach((authority: string) => {
      if (authority === "ADMIN" || authority === "SUPER_ADMIN") {
        localStorage.setItem('tokenConnectedUserWSU1234567GHTVB', raw);
        localStorage.setItem("administratorauthorization", authority);
        localStorage.setItem('inTheRoleOf', authority);
      }
      else if (authority === "GESTIONNAIRE") {
        localStorage.setItem('tokenConnectedUserWSU1234567GHTVB', raw);
        localStorage.setItem("tokenConnectedUserGestionaire", raw);
        localStorage.setItem('inTheRoleOf', authority);
      }
      else if (authority === "USER") {
        localStorage.setItem('tokenConnectedUserWSU1234567GHTVB', raw);
        localStorage.setItem('tokenConnectedUserOnline', raw);
        localStorage.setItem('inTheRoleOf', authority);
        localStorage.setItem('securityCodeExistForUser', String(secure));
        localStorage.setItem('confirmphoneIsOkForThisUser', String(confirmphone));
        localStorage.setItem('confirmCNiIstOkForThisUser', String(confirmCNi));
      }
      else if (authority === "CAISSIER") {
        localStorage.setItem('tokenConnectedUserWSU1234567GHTVB', raw);
        localStorage.setItem('tokenConnectedCaissieronline', raw);
        localStorage.setItem('inTheRoleOf', authority);
      }
      else if (
        authority === "MICROFINANCE" ||
        authority === "VENDOR" ||
        authority === "PLATFORM_ADMIN" ||
        authority === "SPAY_OPERATOR"
      ) {
        localStorage.setItem('tokenConnectedUserWSU1234567GHTVB', raw);
        localStorage.setItem('tokenConnectedPartneronline', raw);
        localStorage.setItem('PARTNERROLEIST', authority);
        localStorage.setItem('inTheRoleOf', authority);
      } else if (authority === "BANK") {
        localStorage.setItem('tokenConnectedUserWSU1234567GHTVB', raw);
        localStorage.setItem('inTheRoleOf', authority);
      }
    });
  }

  loadToken() {
    return this.jwtToken = localStorage.getItem('tokenConnectedUserWSU1234567GHTVB');
  }

  getOrCreateDeviceId(): string {
    const existing = (localStorage.getItem(this.deviceIdStorageKey) || '').trim();
    if (existing) {
      return existing;
    }

    const generated = this.generateDeviceId();
    localStorage.setItem(this.deviceIdStorageKey, generated);
    return generated;
  }

  private generateDeviceId(): string {
    const prefix = 'spay-web';
    const randomPart = this.safeRandomUuid();
    return `${prefix}-${randomPart}`;
  }

  private safeRandomUuid(): string {
    const browserCrypto = (window as any)?.crypto;
    if (browserCrypto && typeof browserCrypto.randomUUID === 'function') {
      return browserCrypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}
